// Music theory for the soundtrack (songs.ts) and its player: scales, chords, note names, a deterministic hash for
// the small variations that keep a loop alive. Pure: tested without WebAudio, and the same bar is the same everywhere.

export type ScaleName = 'major' | 'minor' | 'dorian' | 'pentMajor' | 'pentMinor';

/** Semitones of each scale above its root. */
export const SCALES: Record<ScaleName, readonly number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  pentMajor: [0, 2, 4, 7, 9],
  pentMinor: [0, 3, 5, 7, 10],
};

/** MIDI note numbers of the natural notes in octave 0, for `n('D4')`. */
const NAMES: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** MIDI number of a note by name: `n('D4')` is 62, `n('F#5')` 78, `n('Bb2')` 46. */
export function n(name: string): number {
  const m = /^([A-G])(#|b)?(-?\d)$/.exec(name);
  if (!m) throw new Error(`bad note ${name}`);
  return 12 * (Number(m[3]) + 1) + NAMES[m[1]] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0);
}

/** Whether a MIDI note belongs to the scale on `root` (any octave). */
export const inScale = (note: number, root: number, scale: ScaleName): boolean => SCALES[scale].includes((((note - root) % 12) + 12) % 12);

/** A chord: its notes (MIDI) from a root and intervals, `oct` octaves up. */
export const chord = (root: number, intervals: readonly number[], oct = 0): number[] => intervals.map(i => root + i + 12 * oct);
export const MAJ = [0, 4, 7], MIN = [0, 3, 7], MAJ7 = [0, 4, 7, 11], MIN7 = [0, 3, 7, 10], DOM7 = [0, 4, 7, 10], SUS4 = [0, 5, 7], SUS2 = [0, 2, 7], ADD9 = [0, 4, 7, 14], MIN9 = [0, 3, 7, 10, 14];

/**
 * A chord's notes spread over `count` positions upward from its lowest note, cycling through the chord tones an
 * octave higher each round: the material of an arpeggio.
 */
export function spread(notes: readonly number[], count: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < count; i++) out.push(notes[i % notes.length] + 12 * Math.floor(i / notes.length));
  return out;
}

/** A unit value in [0, 1) from integers, the same every time: the humanisation of a loop must not drift between players. */
export function hash(...xs: number[]): number {
  let h = 2166136261;
  for (const x of xs) { h ^= Math.floor(x) + 0x9e3779b9; h = Math.imul(h ^ (h >>> 15), 0x85ebca6b); h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35); }
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
